import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp, Edit, Trash2, Shield, Zap, Sparkles } from "lucide-react";
import { Tenant } from "../../../shared/interfaces/model/tenant.interface";
import DeleteConfirmationModal from "./DeleteConfirmationModal";


const getTierStyles = (tier: string) => {

  switch (tier.toLowerCase()) {
    case "basic":
      return {
        bgColor: "bg-slate-700",
        hoverBg: "hover:bg-slate-800",
        icon: <Shield className="w-4 h-4 text-white" />,
        textColor: "text-white",
      };
    case "standard":
      return {
        bgColor: "bg-green-500",
        hoverBg: "hover:bg-green-600",
        icon: <Zap className="w-4 h-4 text-white" />,
        textColor: "text-white",
      };
    case "pro":
      return {
        bgColor: "bg-gradient-to-r from-purple-600 to-indigo-600",
        hoverBg: "hover:from-purple-700 hover:to-indigo-700",
        icon: <Sparkles className="w-4 h-4 text-white" />,
        textColor: "text-white",
      };
    default:
      return {
        bgColor: "bg-gray-500",
        hoverBg: "hover:bg-gray-600",
        icon: <Shield className="w-4 h-4 text-white" />,
        textColor: "text-white",
      };
  }
};

const UserTable = ({
  tenantsList,
  tenantListIsOk,
  changeCurrentTenant,
  getDetailTenant,
  deleteTenantRow,
}: {
  tenantsList: Tenant[];
  tenantListIsOk: boolean;
  changeCurrentTenant: (tenant: Tenant) => void;
  getDetailTenant: (tenantId: string | undefined) => void;
  deleteTenantRow: (tenantId: string) => void;
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  const handleDeleteClick = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowConfirmationModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedTenant?.tenantId) {
      deleteTenantRow(selectedTenant.tenantId);
    }
    setShowConfirmationModal(false);
    setSelectedTenant(null);
  };

  const getStatus = (status: string) => {
    if (status === "Active") {
      return (
        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
          {status}
        </span>
      )
    } else if (status === "Inactive") {
      return (
        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
          {status}
        </span>
      )
    } else if (status === "Pending" || status === "In progress") {
      return (
        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
          {status}
        </span>
      )
    } else {
      return (
        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
          {status}
        </span>
      )
    }
  }

  const renderProductList = () => {
    return (
      <motion.tbody
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="bg-white divide-y divide-gray-100"
      >
        {tenantListIsOk &&
          tenantsList.filter((tenant) => tenant.isUser).map((item: Tenant) => (
            <motion.tr
              key={item.id + "_" + item.status}
              style={{ border: "1px solid ##0c1d46", borderRadius: "10px" }}
              whileHover={{ backgroundColor: "#0c1d46", color: "#f2f2f2" }}
              transition={{ duration: 0.1 }}
              className="group"
            >
              <td className="px-6 py-5 whitespace-nowrap">
                <div className="text-sm font-medium">
                  {item.organizationName}
                </div>
              </td>
              <td className="px-6 py-5 whitespace-nowrap">
                <div className="flex flex-wrap gap-2">
                  {Array.isArray(item.product) ? (
                    item.product.map((prod) => (
                      <span
                        key={item.id.concat("_", item.status + "_" + prod)}
                        className="inline-flex items-center rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {prod}
                      </span>
                    ))
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {item.product}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-5 whitespace-nowrap text-sm relative">
                {item.tier && (
                  <div className="relative group">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`
                    inline-flex items-center gap-2 px-3 py-1.5
                    rounded-md cursor-pointer transition-all duration-200
                    ${getTierStyles(item.tier).bgColor}
                    ${getTierStyles(item.tier).hoverBg}
                    shadow-sm hover:shadow-md
                    `}
                    >
                      {getTierStyles(item.tier).icon}
                      <span
                        className={`text-sm font-medium ${getTierStyles(item.tier).textColor}`}
                      >
                        {item.tier}
                      </span>
                      {item.tier.toLowerCase() === "pro" && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold leading-none text-white bg-red-500 rounded-full shadow-md"
                        >
                          PRO
                        </motion.span>
                      )}
                    </motion.div>
                  </div>
                )}
              </td>
              <td className="px-6 py-5 whitespace-nowrap">
                {item.status && getStatus(item.status)}
              </td>
              <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500 group-hover:text-white">
                {item.creationDate}
              </td>
              <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500 group-hover:text-white">
                {item.subscriptionExpirationDate}
              </td>
              <td className="px-6 py-5 whitespace-nowrap text-right text-sm font-medium">
                <button
                  onClick={() => {
                    window.localStorage.setItem(
                      "tenantId",
                      item.tenantId ?? ""
                    );
                    window.localStorage.setItem("isUpdateModeOn", "true");
                    getDetailTenant(item.tenantId);
                  }}
                  className="bg-indigo-900 text-white mr-4 transition-colors duration-200 group-hover:text-white group-hover:bg-indigo-900 rounded-full p-2"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDeleteClick(item)}
                  className="text-white transition-colors duration-200 group-hover:text-white bg-red-500 rounded-full p-2"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </td>
            </motion.tr>
          ))}
      </motion.tbody>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col w-full bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 mt-9"
    >
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  "Organization Name",
                  "Product",
                  "Version",
                  "Status",
                  "Creation Date",
                  "Expiration Date",
                  "Actions",
                ].map((header, index) => (
                  <th
                    key={index}
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <motion.div
                      className="flex items-center cursor-pointer"
                      whileHover={{ color: "#374151" }}
                    >
                      {header}
                      {index < 3 && (
                        <span className="ml-2">
                          <ChevronUp className="w-4 h-4" />
                        </span>
                      )}
                    </motion.div>
                  </th>
                ))}
              </tr>
            </thead>
            {renderProductList()}
          </table>
          <DeleteConfirmationModal
            isOpen={showConfirmationModal}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={handleConfirmDelete}
            organizationName={selectedTenant?.organizationName || ""}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default UserTable;
